// src/pages/SellersPage.jsx
import React from 'react';

export default function SellersPage() {
  const year = new Date().getFullYear();

  const steps = [
    { n: 1, t: 'Free valuation', d: 'We assess your home and advise on pricing, strategy and timing.' },
    { n: 2, t: 'Prep & media', d: 'Pro photos, floorplan and copy that sells. We’ll suggest simple win-wins.' },
    { n: 3, t: 'Launch & viewings', d: 'Your listing goes live with accompanied viewings and rapid feedback.' },
    { n: 4, t: 'Offers & negotiation', d: 'We qualify buyers and negotiate firmly to maximise your sale price.' },
    { n: 5, t: 'Sales progression', d: 'We coordinate with solicitors, surveyors and lenders to keep momentum.' },
    { n: 6, t: 'Exchange & completion', d: 'Paperwork wrapped, keys handed over. On time, no surprises.' },
  ];

  const faqs = [
    {
      q: 'How quickly can you list my property?',
      a: 'Often within 48–72 hours once photos and floorplans are complete. We can fast-track where needed.',
    },
    { q: 'Do you handle viewings?', a: 'Yes. We accompany viewings, collect feedback and keep you fully updated.' },
    {
      q: 'What are your fees?',
      a: 'Transparent, success-based. Get a personalised quote during your free valuation.',
    },
    {
      q: 'Do you help with conveyancing?',
      a: 'We can recommend local conveyancers and work closely to keep your sale progressing smoothly.',
    },
  ];

  return (
    <>
      <style>{`
        /* ===== Sellers Page (namespaced) ===== */
        .mrm-sell { --brand:#1e3a8a; --brand2:#0ea5e9; --ink:#0f172a; --muted:#475569; --paper:#fff; --bg:#f8fafc; --line:#e5e7eb; font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; color:var(--ink); }
        .mrm-sell a { color: var(--brand); text-decoration: none; }
        .mrm-sell a:hover { text-decoration: underline; }

        .mrm-sell__container { max-width:1200px; margin:0 auto; padding: 1rem; }
        .mrm-sell__eyebrow { display:inline-block; padding:.35rem .75rem; border:1px solid rgba(255,255,255,.5); border-radius:999px; background:rgba(255,255,255,.12); font-weight:900; letter-spacing:.08em; text-transform:uppercase; color:#fff; font-size:.78rem; }

        /* HERO */
        .mrm-sell__hero {
          position:relative; color:#fff; min-height:66vh; display:grid; align-items:center; isolation:isolate;
          background-image:
            linear-gradient(180deg, rgba(2,6,23,.25) 0%, rgba(2,6,23,.65) 100%),
            url('/media/hero-property-poster.jpg');
          background-size:cover; background-position:center;
        }
        .mrm-sell__hero h1 { margin:.5rem 0; font-weight:900; line-height:1.05; font-size: clamp(1.8rem, 4.2vw, 3rem); text-shadow:0 2px 18px rgba(0,0,0,.25); }
        .mrm-sell__lede { max-width: 780px; font-size: clamp(1rem, 2vw, 1.15rem); opacity:.95; }

        .mrm-sell__cta { display:flex; gap:.8rem; flex-wrap:wrap; margin-top:1.25rem; }
        .mrm-sell__btn {
          display:inline-flex; align-items:center; justify-content:center; gap:.5rem; font-weight:900; border-radius:999px; padding:.9rem 1.2rem; cursor:pointer; border:1px solid rgba(255,255,255,.7); color:#fff; background:transparent; transition:.2s transform,.25s box-shadow,.2s background;
        }
        .mrm-sell__btn--primary { background: var(--brand); border-color:var(--brand); }
        .mrm-sell__btn:hover { transform: translateY(-1px); box-shadow:0 10px 22px rgba(0,0,0,.25); }
        .mrm-sell__chips { display:flex; gap:.5rem; flex-wrap:wrap; margin-top:1rem; }
        .mrm-sell__chip { background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.3); color:#fff; padding:.4rem .6rem; border-radius:10px; font-weight:800; font-size:.84rem; }

        /* SECTION HEADERS */
        .mrm-sell__section { padding: clamp(3rem, 6vw, 5.5rem) 0; background: var(--bg); }
        .mrm-sell__section--alt { background:#fff; }
        .mrm-sell__title { margin:0; font-weight:900; font-size: clamp(1.4rem, 3vw, 2rem); color: var(--brand); }
        .mrm-sell__sub { margin:.35rem 0 1rem; color: var(--muted); max-width: 900px; }

        /* SERVICES GRID */
        .mrm-sell__grid { display:grid; grid-template-columns:1fr; gap:1rem; margin-top:1rem; }
        @media (min-width: 760px){ .mrm-sell__grid { grid-template-columns:1fr 1fr; } }
        @media (min-width: 1100px){ .mrm-sell__grid { grid-template-columns:1fr 1fr 1fr 1fr; } }
        .mrm-sell__card { background:#fff; border:1px solid var(--line); border-radius:14px; box-shadow:0 10px 24px rgba(2,6,23,.06); padding:1rem; display:grid; gap:.5rem; height:100%; }
        .mrm-sell__icon { width:44px; height:44px; border-radius:12px; display:grid; place-items:center; background: #dbeafe; color:#0b3aa4; font-weight:900; font-size:1.15rem; }
        .mrm-sell__card h3 { margin:.2rem 0 0; font-size:1rem; font-weight:900; color:var(--ink); }
        .mrm-sell__card p { margin:0; color: #475569; font-size:.95rem; line-height:1.55; }

        /* TIMELINE */
        .mrm-sell__timeline { display:grid; grid-template-columns:1fr; gap:1rem; margin-top:1rem; }
        @media (min-width:760px){ .mrm-sell__timeline { grid-template-columns:1fr 1fr 1fr; } }
        .mrm-sell__step { background:#fff; border:1px solid var(--line); border-radius:14px; padding:1rem; display:flex; gap:.9rem; }
        .mrm-sell__num { flex:0 0 auto; width:36px; height:36px; border-radius:50%; background:var(--brand); color:#fff; font-weight:900; display:grid; place-items:center; }
        .mrm-sell__step h4 { margin:.1rem 0 .1rem; font-size:1rem; font-weight:900; }
        .mrm-sell__step p { margin:0; color:#475569; font-size:.95rem; }

        /* STRIP (logos) */
        .mrm-sell__strip { border:1px solid var(--line); border-radius:14px; padding:.9rem; background:#fff; display:grid; grid-template-columns: repeat(2, 1fr); gap:.7rem; }
        @media (min-width:760px){ .mrm-sell__strip{ grid-template-columns: repeat(6, 1fr); } }
        .mrm-sell__badge { background:#f1f5f9; border:1px solid var(--line); border-radius:12px; height:64px; display:grid; place-items:center; color:#0f172a; font-weight:900; }

        /* TESTIMONIALS */
        .mrm-sell__quotes { display:grid; grid-template-columns:1fr; gap:1rem; }
        @media (min-width:900px){ .mrm-sell__quotes { grid-template-columns:1fr 1fr; } }
        .mrm-sell__quote { background:#fff; border:1px solid var(--line); border-radius:14px; padding:1rem; }
        .mrm-sell__quote blockquote { margin:0 0 .6rem; color:#0f172a; line-height:1.7; font-size:1.02rem; }
        .mrm-sell__quote cite { color:#475569; font-style:normal; font-size:.9rem; }

        /* FAQ */
        .mrm-sell__faq { display:grid; grid-template-columns:1fr; gap:.7rem; }
        .mrm-sell details { background:#fff; border:1px solid var(--line); border-radius:12px; padding:.7rem 1rem; }
        .mrm-sell summary { cursor:pointer; font-weight:900; list-style:none; }
        .mrm-sell summary::-webkit-details-marker{ display:none; }
        .mrm-sell details[open] { box-shadow:0 10px 22px rgba(2,6,23,.06); }

        /* CTA BANNER */
        .mrm-sell__banner {
          color:#fff; border-radius:18px; overflow:hidden; position:relative; padding: clamp(1.2rem, 4vw, 2rem);
          background: linear-gradient(135deg, var(--brand), var(--brand2));
        }
        .mrm-sell__banner h3 { margin:.2rem 0; font-size: clamp(1.2rem, 2.4vw, 1.6rem); font-weight:900; }
        .mrm-sell__banner p { margin:.2rem 0 1rem; opacity:.95; }
        .mrm-sell__btn--light { background:#fff; color:var(--brand); border-color:#fff; }

        /* FOOT NOTES */
        .mrm-sell__foot { color:#64748b; font-size:.85rem; margin-top:.6rem; }

        /* Back to top */
        .mrm-sell__top { position:fixed; right:18px; bottom:18px; border:none; background:var(--brand); color:#fff; width:44px; height:44px; border-radius:50%; cursor:pointer; box-shadow:0 10px 22px rgba(2,6,23,.25); font-weight:900; }
        .mrm-sell__top:hover{ transform: translateY(-1px); }
      `}</style>

      <main className="mrm-sell">
        {/* HERO */}
        <header className="mrm-sell__hero" aria-label="Sell your property hero">
          <div className="mrm-sell__container">
            <span className="mrm-sell__eyebrow">Thinking of selling?</span>
            <h1>Sell with confidence. <span style={{ color: '#c8d5f3' }}>Local advice, clearly delivered.</span></h1>
            <p className="mrm-sell__lede">
              MRM Lettings Property &amp; Management Ltd — one dedicated Middlesbrough office, local knowledge. Professional
              marketing, proactive negotiation and clear updates from valuation to completion.
            </p>
            <div className="mrm-sell__cta">
              <a href="/sellers/valuation" className="mrm-sell__btn">Start your free valuation</a>
              <a href="/contact" className="mrm-sell__btn">Book a market appraisal</a>
            </div>
          </div>
        </header>

        {/* SERVICES */}
        <section className="mrm-sell__section">
          <div className="mrm-sell__container">
            <h2 className="mrm-sell__title">What you’ll get with us</h2>
            <p className="mrm-sell__sub">A complete service designed to maximise price and minimise stress.</p>

            <div className="mrm-sell__grid">
              <article className="mrm-sell__card">
                <div className="mrm-sell__icon" aria-hidden="true">💷</div>
                <h3>Free valuation</h3>
                <p>Accurate pricing informed by live buyer demand and comparable sales.</p>
              </article>

              <article className="mrm-sell__card">
                <div className="mrm-sell__icon" aria-hidden="true">📣</div>
                <h3>Tailored marketing</h3>
                <p>Pro photography, floorplans and copy that sells. Social ads where buyers actually look.</p>
              </article>

              <article className="mrm-sell__card">
                <div className="mrm-sell__icon" aria-hidden="true">📈</div>
                <h3>Smart strategy</h3>
                <p>Launch plan and feedback loops that build momentum and protect your price.</p>
              </article>

              <article className="mrm-sell__card">
                <div className="mrm-sell__icon" aria-hidden="true">⭐</div>
                <h3>Negotiation & care</h3>
                <p>Qualified offers, firm negotiation and hands-on progression to completion.</p>
              </article>
            </div>
          </div>
        </section>

        {/* PROCESS */}
        <section className="mrm-sell__section mrm-sell__section--alt">
          <div className="mrm-sell__container">
            <h2 className="mrm-sell__title">Your selling journey</h2>
            <p className="mrm-sell__sub">Clear steps, transparent updates and zero jargon.</p>

            <div className="mrm-sell__timeline">
              {steps.map((s) => (
                <article className="mrm-sell__step" key={s.n}>
                  <div className="mrm-sell__num">{s.n}</div>
                  <div>
                    <h4>{s.t}</h4>
                    <p>{s.d}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* WHERE WE MARKET */}
        <section className="mrm-sell__section">
          <div className="mrm-sell__container">
            <h2 className="mrm-sell__title">We market where buyers are</h2>
            <p className="mrm-sell__sub">Major portals and targeted social campaigns to reach the widest audience quickly.</p>

            <div className="mrm-sell__strip" aria-label="Partner and portal logos">
              {['Rightmove', 'Zoopla', 'OnTheMarket', 'Facebook', 'Instagram', 'Google'].map((n) => (
                <div className="mrm-sell__badge" key={n}>{n}</div>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="mrm-sell__section mrm-sell__section--alt">
          <div className="mrm-sell__container">
            <h2 className="mrm-sell__title">Sellers who moved with us</h2>
            <p className="mrm-sell__sub">A few words from local homeowners.</p>

            <div className="mrm-sell__quotes">
              <figure className="mrm-sell__quote">
                <blockquote>
                  “Professional from start to finish. Great photos, lots of viewings and we achieved over asking. Highly recommend.”
                </blockquote>
                <cite>— L. Thompson, Middlesbrough</cite>
              </figure>
              <figure className="mrm-sell__quote">
                <blockquote>
                  “Communication was brilliant and the sales progression kept everything on track. Couldn’t have asked for more.”
                </blockquote>
                <cite>— J. Ahmed, Stockton-on-Tees</cite>
              </figure>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mrm-sell__section">
          <div className="mrm-sell__container">
            <h2 className="mrm-sell__title">Selling FAQs</h2>
            <p className="mrm-sell__sub">Answers to common questions — and we’re just a call away if you’d like to talk.</p>

            <div className="mrm-sell__faq">
              {faqs.map((f, i) => (
                <details key={i}>
                  <summary>{f.q}</summary>
                  <div style={{ marginTop: '.4rem', color: '#475569' }}>{f.a}</div>
                </details>
              ))}
            </div>

            <p className="mrm-sell__foot">*Stats indicative; results vary with market conditions and property type.</p>
          </div>
        </section>

        {/* CTA BANNER */}
        <section className="mrm-sell__section mrm-sell__section--alt" aria-label="Final call to action">
          <div className="mrm-sell__container">
            <div className="mrm-sell__banner">
              <h3>Ready to move in {year}? Get your free valuation today.</h3>
              <p>Fast online estimate in minutes or book an in-person appraisal with our local expert.</p>
              <div className="mrm-sell__cta">
                <a href="/sellers/valuation" className="mrm-sell__btn mrm-sell__btn--light">Start valuation</a>
                <a href="/contact" className="mrm-sell__btn" style={{ borderColor: 'rgba(255,255,255,.85)', color:'#fff' }}>Talk to us</a>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Back to top */}
      <button
        className="mrm-sell__top"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Back to top"
        title="Back to top"
      >
        ↑
      </button>
    </>
  );
}
